import { useGSAP } from '@gsap/react';
import ScrollToPlugin from 'gsap/dist/ScrollToPlugin';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { gsap } from 'gsap/dist/gsap';
import Image from 'next/image';
import { FC, useRef } from 'react';

import { BusinessHubOnlinePaymentStep } from './business-hub-online-payment-step';
import styles from './business-hub-online-payment.module.scss';

gsap.registerPlugin(ScrollToPlugin);
gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(useGSAP);

export interface OnlinePaymentStep {
    name: string;
    description: string;
    icon: string;
    screenImage: string;
    illustration: string;
    illustrationRatio?: string;
}

export interface BusinessHubOnlinePaymentProps {
    title: string;
    description: string;
    backgroundColor?: string;
    steps: OnlinePaymentStep[];
}

const BusinessHubOnlinePayment: FC<BusinessHubOnlinePaymentProps> = (props) => {
    const { title, description, backgroundColor, steps = [] } = props;

    const containerRef = useRef<HTMLDivElement>(null);

    const scollTo = (id: string) => {
        if (window.location.hash.startsWith(`#${id}`)) {
            setTimeout(() => {
                const elem = document.getElementById(id);
                if (!elem) return;
                const position = elem.getBoundingClientRect();
                gsap.to(window, { duration: 1, scrollTo: position.top + window.scrollY - 116 });
            }, 500);
        }
    };

    useGSAP(
        () => {
            let mm = gsap.matchMedia();
            mm.add(
                {
                    reducedMotion: '(prefers-reduced-motion: no-preference)',
                },
                () => {
                    const selector = gsap.utils.selector(containerRef.current);
                    const sectionContainer = gsap.utils.toArray<HTMLDivElement>(selector(`.${styles.sections}`));
                    const sections = gsap.utils.toArray<HTMLDivElement>(selector(`.${styles.stepWrapper}`));
                    const progresses = gsap.utils.toArray<HTMLDivElement>(selector(`.${styles.progressItem}`));
                    const screens = gsap.utils.toArray<HTMLDivElement>(selector(`.${styles.phoneScreenBox}`));
                    const titles = gsap.utils.toArray<HTMLDivElement>(selector(`.${styles.title}`));
                    const descriptions = gsap.utils.toArray<HTMLDivElement>(selector(`.${styles.description}`));

                    gsap.timeline({
                        scrollTrigger: {
                            trigger: containerRef.current,
                            pinnedContainer: containerRef.current,
                            pin: true,
                            start: 'bottom bottom',
                            end: `+=${sections.length * 100}%`,
                            scrub: 1,
                        },
                    });

                    let masterTl = gsap.timeline({
                        defaults: { ease: 'sine.inOut' },
                        scrollTrigger: {
                            trigger: containerRef.current,
                            start: 'top+=20% center+=45%',
                            end: `+=${sections.length * 100}%`,
                            scrub: 1.5,
                            invalidateOnRefresh: true,
                        },
                    });

                    gsap.set(sections, {
                        autoAlpha: 0,
                        y: (i) => {
                            return `${100 * i}vh`;
                        },
                        transformOrigin: 'top',
                        xPercent: (index) => -100 * index,
                    });
                    gsap.set([titles, descriptions, sectionContainer], { autoAlpha: 0 });
                    gsap.set(screens, { autoAlpha: 0, zIndex: (index) => sections.length - index });
                    gsap.set(progresses, { yPercent: 100, zIndex: (index) => sections.length - index });

                    masterTl.to([titles, descriptions, sectionContainer], {
                        ease: 'power4.in',
                        autoAlpha: 1,
                        z: 0,
                        stagger: 0.05,
                    });

                    for (let index = 0; index < sections.length; index++) {
                        const section = sections[index];
                        const screen = screens[index];
                        const progress = progresses[index];
                        const prevScreen = screens[index - 1];
                        const prevSection = sections[index - 1];
                        const prevProgress = progresses[index - 1];
                        const nextProgress = progresses[index + 1];

                        if (prevSection) {
                            masterTl
                                .to(
                                    [prevSection, prevProgress],
                                    {
                                        yPercent: -200,
                                        autoAlpha: 0,
                                    },
                                    index == 0 ? 0 : '+=1',
                                )
                                .to(
                                    prevScreen,
                                    {
                                        autoAlpha: 0,
                                    },
                                    '<',
                                );
                        }

                        if (nextProgress) {
                            masterTl.to(
                                nextProgress,
                                {
                                    yPercent: 50,
                                    autoAlpha: 1,
                                },
                                '<',
                            );
                        }

                        masterTl
                            .to(
                                [section, progress],
                                {
                                    autoAlpha: 1,
                                    y: 0,
                                    yPercent: 0,
                                    onComplete: (progress) => {
                                        progress.classList.add(styles.active);
                                    },
                                    onReverseComplete: (progress) => {
                                        progress.classList.remove(styles.active);
                                    },
                                    onCompleteParams: [progress],
                                    onReverseCompleteParams: [progress],
                                },
                                index == 0 ? 0 : '<',
                            )
                            .to(
                                screen,
                                {
                                    autoAlpha: 1,
                                },
                                '<',
                            );
                    }
                },
            );

            scollTo('solutions-for-the-omnichannel-world');
            scollTo('business-hub');
        },
        { scope: containerRef },
    );

    if (!steps.length) return null;

    return (
        <div className={styles.container} style={{ '--data-bg-color': backgroundColor } as any}>
            <div className={styles.background} />

            <div className={styles.box} ref={containerRef}>
                <div className={styles.titleBox}>
                    <div className={styles.title}>{title}</div>
                    <div className={styles.description}>{description}</div>
                </div>

                <div className={styles.sections}>
                    <div className={styles.overlay}>
                        <div />
                        <div />
                        <div className={styles.phoneFrame}>
                            <Image
                                src="https://cdn.builder.io/api/v1/image/assets%2F035a3d41874344d79b6c7499e2e7a5bc%2Fe42c03b90c6c471194ffe2a8f6735893"
                                alt=""
                                style={{ objectFit: 'contain', objectPosition: 'center' }}
                                fill={true}
                                loading={'eager'}
                                loader={({ src, width, quality }) => {
                                    return `${src}?width=${width}&quality=${quality ?? 75}&format=webp`;
                                }}
                            />
                            <div className={styles.phoneScreen}>
                                {steps.map((step, index) => (
                                    <div
                                        className={styles.phoneScreenBox}
                                        key={step.name + index}
                                        style={{
                                            backgroundImage: `url(${step.screenImage}?quality=75&format=webp)`,
                                        }}
                                    />
                                ))}
                            </div>
                        </div>

                        <div />
                        <div className={styles.progress}>
                            {steps.map((_, index) => (
                                <div key={index} className={styles.progressItem}>
                                    <div className={styles.progressBox}>
                                        <span>{index + 1}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={styles.sectionContainer}>
                        {steps.map((step, index) => (
                            <BusinessHubOnlinePaymentStep key={step.name + index} {...step} className={styles.stepWrapper} contentClassName={styles.stepBox} />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BusinessHubOnlinePayment;
